import React, { useState } from 'react';
import Icon from '../../../AppIcon';
import Button from '../../../ui/Button';
import Input from '../../../ui/Input';

const ReferralStatusTable = ({ referrals }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  const statusOptions = [
    { value: 'all', label: 'All Status', count: referrals?.length },
    { value: 'pending', label: 'Pending', count: referrals?.filter(r => r?.status === 'pending')?.length },
    { value: 'reviewing', label: 'Under Review', count: referrals?.filter(r => r?.status === 'reviewing')?.length },
    { value: 'matched', label: 'Matched', count: referrals?.filter(r => r?.status === 'matched')?.length },
    { value: 'completed', label: 'Completed', count: referrals?.filter(r => r?.status === 'completed')?.length },
    { value: 'rejected', label: 'Rejected', count: referrals?.filter(r => r?.status === 'rejected')?.length }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: 'Clock' },
      reviewing: { color: 'bg-blue-100 text-blue-800', icon: 'Eye' },
      matched: { color: 'bg-green-100 text-green-800', icon: 'CheckCircle' },
      completed: { color: 'bg-emerald-100 text-emerald-800', icon: 'Trophy' },
      rejected: { color: 'bg-red-100 text-red-800', icon: 'XCircle' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={12} className="mr-1" />
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const filteredReferrals = referrals?.filter(referral => {
      const matchesSearch = referral?.startupName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           referral?.corporateProgram?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      const matchesStatus = statusFilter === 'all' || referral?.status === statusFilter;
      return matchesSearch && matchesStatus;
    })?.sort((a, b) => {
      let aValue = a?.[sortBy];
      let bValue = b?.[sortBy];
      
      if (sortBy === 'date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Referral Status Tracking</h3>
            <p className="text-sm text-muted-foreground">Monitor the progress of all your referrals</p>
          </div>
          <Button variant="outline" size="sm">
            <Icon name="Filter" size={16} className="mr-2" />
            Advanced Filters
          </Button>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Search by startup name or program..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full"
            />
          </div>
          
          <div className="flex items-center space-x-2 overflow-x-auto">
            {statusOptions?.map((option) => (
              <button
                key={option?.value}
                onClick={() => setStatusFilter(option?.value)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  statusFilter === option?.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                }`}
              >
                <span>{option?.label}</span>
                <span className="bg-background/20 text-xs px-1.5 py-0.5 rounded-full">
                  {option?.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('startupName')}
                  className="flex items-center space-x-1 hover:text-foreground transition-colors"
                >
                  <span>Startup</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('corporateProgram')}
                  className="flex items-center space-x-1 hover:text-foreground transition-colors"
                >
                  <span>Program</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">Status</th>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('commission')}
                  className="flex items-center space-x-1 hover:text-foreground transition-colors"
                >
                  <span>Commission</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('date')}
                  className="flex items-center space-x-1 hover:text-foreground transition-colors"
                >
                  <span>Date</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredReferrals?.map((referral) => (
              <tr key={referral?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary/20 to-accent/20 rounded-lg flex items-center justify-center">
                      <Icon name="Building2" size={16} className="text-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{referral?.startupName}</div>
                      <div className="text-sm text-muted-foreground">{referral?.industry}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <div>
                    <div className="font-medium text-foreground">{referral?.corporateProgram}</div>
                    <div className="text-sm text-muted-foreground">{referral?.corporateName}</div>
                  </div>
                </td>
                <td className="p-4">
                  {getStatusBadge(referral?.status)}
                </td>
                <td className="p-4">
                  <div className="font-medium text-foreground">${referral?.commission?.toLocaleString()}</div>
                  <div className="text-sm text-muted-foreground">{referral?.commissionType}</div>
                </td>
                <td className="p-4">
                  <div className="text-sm text-foreground">{referral?.date}</div>
                  <div className="text-xs text-muted-foreground">{referral?.timeAgo}</div>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <Button variant="ghost" size="sm">
                      <Icon name="Eye" size={14} />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Icon name="MessageSquare" size={14} />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Icon name="MoreHorizontal" size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Footer */}
      <div className="p-4 border-t border-border flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Showing {filteredReferrals?.length} of {referrals?.length} referrals
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" disabled>
            <Icon name="ChevronLeft" size={16} />
          </Button>
          <span className="text-sm text-muted-foreground">Page 1 of 1</span>
          <Button variant="outline" size="sm" disabled>
            <Icon name="ChevronRight" size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReferralStatusTable;